/**
 * Chart Store
 * ============
 * Zustand store for chart state management.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Chart, ToothData, SiteData, SiteCode, ChartSummaryStats, RestorativeData } from '@/types';
import * as chartsApi from '@/api/charts';

// =============================================================================
// TYPES
// =============================================================================

interface ChartState {
  // Current chart data
  chart: Chart | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  
  // Dirty state tracking
  isDirty: boolean;
  lastSavedAt: Date | null;
  lastAutosaveAt: Date | null;
  autosaveCount: number;
  
  // Selection state
  selectedTooth: number | null;
  selectedSite: SiteCode | null;
  
  // NEW: Temporary local tooth data (not yet saved to backend)
  tempToothData: Record<number, ToothData>;
  
  // Collaboration
  activeUsers: Array<{ user_id: string; user_name: string }>;
  lockedBy: { user_id: string; user_name: string } | null;
  
  // Input tracking
  inputMethod: 'manual' | 'voice' | null;
  
  // Actions
  loadChart: (chartId: string) => Promise<void>;
  createChart: (patientId: string, isBaseline?: boolean) => Promise<string>;
  updateToothData: (toothNumber: number, data: Partial<ToothData>) => void;
  updateSiteData: (toothNumber: number, siteCode: SiteCode, data: Partial<SiteData>) => void;
  updateRestorativeData: (toothNumber: number, restorativeUpdates: Partial<RestorativeData>) => void; // ADD THIS LINE
  getTempToothData: (toothNumber: number) => ToothData | null;
  clearTempData: () => void;
  setClinicalNotes: (notes: string) => void;
  setAAPClassification: (stage: string, grade: string, extent: string) => void;
  setRiskFactor: (factor: string, enabled: boolean) => void; // NEW
  setReferral: (recommended: boolean, reason?: string) => void; // NEW
  // Selection actions
  selectTooth: (toothNumber: number | null) => void;
  selectSite: (siteCode: SiteCode | null) => void;
  setInputMethod: (method: 'manual' | 'voice' | null) => void;
  // Autosave
  saveChart: () => Promise<void>; // Add explicit save function
  triggerAutosave: () => Promise<void>;
  
  // Finalization
  finalizeChart: (data: chartsApi.ChartFinalizeRequest) => Promise<void>;
  
  // Lock management
  acquireLock: () => Promise<boolean>;
  releaseLock: () => Promise<void>;
  
  // Collaboration
  handleUserJoined: (user: { user_id: string; user_name: string }) => void;
  handleUserLeft: (userId: string) => void;
  handleRemoteToothUpdate: (toothNumber: number, updates: Partial<ToothData>, updatedBy: string) => void;
  
  // Chart management
  setChart: (chartData: Chart) => void;
  reset: () => void;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState = {
  chart: null,
  isLoading: false,
  isSaving: false,
  error: null,
  isDirty: false,
  lastSavedAt: null,
  lastAutosaveAt: null,
  autosaveCount: 0,
  selectedTooth: null,
  selectedSite: null,
  activeUsers: [],
  lockedBy: null,
  inputMethod: null,
  tempToothData: {}, // NEW: Initialize empty temp data storage
};

// =============================================================================
// STORE
// =============================================================================

export const useChartStore = create<ChartState>()(
  immer((set, get) => ({
    ...initialState,

    loadChart: async (chartId: string) => {
      set({ isLoading: true, error: null });
      try {
        const chart = await chartsApi.getChart(chartId);
        
        console.log('Chart loaded from backend:', chart);
        console.log('Chart tooth_data:', chart.tooth_data);
        
        // Initialize tempToothData from loaded chart
        const tempToothData: Record<number, ToothData> = {};
        chart.tooth_data.forEach(tooth => {
          tempToothData[tooth.tooth_number] = { ...tooth };
          
          // Log restorative data during load
          if (tooth.restorative) {
            console.log(`Loaded tooth ${tooth.tooth_number} with restorative:`, tooth.restorative);
          }
        });
        
        set({
          chart,
          tempToothData, // Load existing data into temp storage
          isLoading: false,
          isDirty: false,
          lastSavedAt: new Date(),
        });
        
        console.log('Chart loaded with temp data:', tempToothData);
      } catch (error: any) {
        set({
          error: error.response?.data?.error?.message || 'Failed to load chart',
          isLoading: false,
        });
        throw error;
      }
    },

    createChart: async (patientId: string, isBaseline = false) => {
      set({ isLoading: true, error: null });
      try {
        const chart = await chartsApi.createChart({
          patient_id: patientId,
          is_baseline: isBaseline,
          exam_type: 'comprehensive',
        });
        set({
          chart,
          isLoading: false,
          isDirty: false,
          lastSavedAt: new Date(),
        });
        return chart.id;
      } catch (error: any) {
        set({
          error: error.response?.data?.error?.message || 'Failed to create chart',
          isLoading: false,
        });
        throw error;
      }
    },

    updateToothData: (toothNumber: number, data: Partial<ToothData>) => {
      set((state) => {
        // Update temporary local data instead of chart
        if (!state.tempToothData[toothNumber]) {
          // Initialize new tooth data
          state.tempToothData[toothNumber] = {
            tooth_number: toothNumber,
            status: 'present',
            mobility: null,
            furcation: null,
            furcation_locations: null,
            plaque_index: null,
            gingival_index: null,
            notes: null,
            sites: [],
            restorative: undefined,
          };
        }
        
        // Merge the update
        const tooth = state.tempToothData[toothNumber];
        if (data.status !== undefined) tooth.status = data.status;
        if (data.mobility !== undefined) tooth.mobility = data.mobility;
        if (data.furcation !== undefined) tooth.furcation = data.furcation;
        if (data.sites !== undefined) tooth.sites = data.sites;
        if (data.notes !== undefined) tooth.notes = data.notes;
        
        // Handle restorative merge specially
        if (data.restorative !== undefined) {
          if (data.restorative === null) {
            tooth.restorative = undefined;
          } else if (tooth.restorative) {
            // Merge nested restorative objects
            tooth.restorative = {
              ...tooth.restorative,
              ...data.restorative,
            };
          } else {
            tooth.restorative = data.restorative;
          }
        }
        
        state.isDirty = true;
        
        console.log('Updated temp data for tooth', toothNumber, ':', state.tempToothData[toothNumber]);
      });
    },

    updateRestorativeData: (toothNumber: number, restorativeUpdates: Partial<RestorativeData>) => {
      set((state) => {
        // Ensure tooth exists in temp data
        if (!state.tempToothData[toothNumber]) {
          state.tempToothData[toothNumber] = {
            tooth_number: toothNumber,
            status: 'present',
            mobility: null,
            furcation: null,
            furcation_locations: null,
            plaque_index: null,
            gingival_index: null,
            notes: null,
            sites: [],
            restorative: restorativeUpdates,
          };
        } else {
          const tooth = state.tempToothData[toothNumber];
          tooth.restorative = {
            ...tooth.restorative,
            ...restorativeUpdates,
          };
        }
        
        state.isDirty = true;
        
        console.log('Updated restorative data for tooth', toothNumber, ':', state.tempToothData[toothNumber].restorative);
      });
    },

    updateSiteData: (toothNumber: number, siteCode: SiteCode, data: Partial<SiteData>) => {
      set((state) => {
        // Ensure tooth exists in temp data
        if (!state.tempToothData[toothNumber]) {
          state.tempToothData[toothNumber] = {
            tooth_number: toothNumber,
            status: 'present',
            mobility: null,
            furcation: null,
            furcation_locations: null,
            plaque_index: null,
            gingival_index: null,
            notes: null,
            sites: [],
            restorative: undefined,
          };
        }
        
        const tooth = state.tempToothData[toothNumber];
        
        if (!tooth.sites) {
          tooth.sites = [];
        }
        
        const siteIndex = tooth.sites.findIndex((s) => s.site === siteCode);
        
        if (siteIndex >= 0) {
          // Update existing site
          tooth.sites[siteIndex] = {
            ...tooth.sites[siteIndex],
            ...data,
          };
        } else {
          // Add new site
          tooth.sites.push({
            site: siteCode,
            pocket_depth: null,
            gingival_margin: null,
            bleeding: false,
            suppuration: false,
            cal: null,
            ...data,
          });
        }
        
        state.isDirty = true;
        
        console.log('Updated site data for tooth', toothNumber, 'site', siteCode);
      });
    },

    getTempToothData: (toothNumber: number) => {
      const state = get();
      return state.tempToothData[toothNumber] || null;
    },

    clearTempData: () => {
      set({ tempToothData: {} });
    },

    setClinicalNotes: (notes: string) => {
      set((state) => {
        if (state.chart) {
          state.chart.clinical_notes = notes;
          state.isDirty = true;
        }
      });
    },

    setAAPClassification: (stage: string, grade: string, extent: string) => {
      set((state) => {
        if (state.chart) {
          state.chart.aap_stage = stage || null;
          state.chart.aap_grade = grade || null;
          state.chart.aap_extent = extent || null;
          state.isDirty = true;
        }
      });
    },

    setRiskFactor: (factor: string, enabled: boolean) => {
      set((state) => {
        if (state.chart) {
          if (!state.chart.risk_factors) {
            state.chart.risk_factors = [];
          }
          
          const index = state.chart.risk_factors.indexOf(factor);
          
          if (enabled && index === -1) {
            // Add risk factor
            state.chart.risk_factors.push(factor);
          } else if (!enabled && index !== -1) {
            // Remove risk factor
            state.chart.risk_factors.splice(index, 1);
          }
          
          state.isDirty = true;
        }
      });
    },

    setReferral: (recommended: boolean, reason?: string) => {
      set((state) => {
        if (state.chart) {
          state.chart.referral_recommended = recommended;
          if (reason !== undefined) {
            state.chart.referral_reason = reason || null;
          }
          state.isDirty = true;
        }
      });
    },

    selectTooth: (toothNumber: number | null) => {
      set((state) => {
        state.selectedTooth = toothNumber;
        state.selectedSite = null;
        
        // Auto-initialize temp data if tooth doesn't exist
        if (toothNumber !== null && !state.tempToothData[toothNumber]) {
          console.log('Auto-creating temp data for tooth', toothNumber);
          state.tempToothData[toothNumber] = {
            tooth_number: toothNumber,
            status: 'present',
            mobility: null,
            furcation: null,
            furcation_locations: null,
            plaque_index: null,
            gingival_index: null,
            notes: null,
            sites: [],
            restorative: undefined,
          };
        }
      });
    },

    selectSite: (siteCode: SiteCode | null) => {
      set({ selectedSite: siteCode });
    },

    setInputMethod: (method: 'manual' | 'voice' | null) => {
      set({ inputMethod: method });
    },

    saveChart: async () => {
      const state = get();
      
      if (!state.chart) {
        console.error('Cannot save: No chart loaded');
        throw new Error('No chart loaded');
      }
      
      if (!state.isDirty) {
        console.log('No changes to save');
        return;
      }
      
      set({ isSaving: true, error: null });
      
      try {
        const { tempToothData, chart } = get();
        const tooth_data = Object.values(tempToothData);
        
        console.log('Saving chart with tooth data (including restorative):', tooth_data);
        
        // Log restorative data specifically for debugging
        tooth_data.forEach(tooth => {
          if (tooth.restorative) {
            console.log(`Tooth ${tooth.tooth_number} restorative data:`, tooth.restorative);
          }
        });
        
        const response = await chartsApi.autosaveChart(chart!.id, {
          tooth_data,
          clinical_notes: chart!.clinical_notes || undefined,
          input_method: get().inputMethod || undefined,
          // AAP Classification
          aap_stage: chart!.aap_stage || undefined,
          aap_grade: chart!.aap_grade || undefined,
          aap_extent: chart!.aap_extent || undefined,
          // Risk Factors
          risk_factors: chart!.risk_factors || undefined,
          // Referral
          referral_recommended: chart!.referral_recommended,
          referral_reason: chart!.referral_reason || undefined,
        });
        
        console.log('Autosave API response:', response);
        console.log('Response tooth_data:', response.tooth_data);
        
        // IMPORTANT: Only sync if backend actually returns tooth_data
        set((draft) => {
          if (draft.chart) {
            // Sync AAP Classification from response
            if (response.aap_stage !== undefined) draft.chart.aap_stage = response.aap_stage;
            if (response.aap_grade !== undefined) draft.chart.aap_grade = response.aap_grade;
            if (response.aap_extent !== undefined) draft.chart.aap_extent = response.aap_extent;
            
            // Sync Risk Factors from response
            if (response.risk_factors !== undefined) draft.chart.risk_factors = response.risk_factors;
            
            // Sync Referral from response
            if (response.referral_recommended !== undefined) draft.chart.referral_recommended = response.referral_recommended;
            if (response.referral_reason !== undefined) draft.chart.referral_reason = response.referral_reason;
            
            // Check if response includes tooth_data
            if (response.tooth_data && Array.isArray(response.tooth_data)) {
              console.log('Syncing backend response back to tempToothData');
              
              // Update chart tooth_data
              draft.chart.tooth_data = response.tooth_data;
              
              // Rebuild tempToothData from backend response
              const newTempToothData: Record<number, ToothData> = {};
              response.tooth_data.forEach(tooth => {
                newTempToothData[tooth.tooth_number] = { ...tooth };
                
                // Log if restorative data is present
                if (tooth.restorative) {
                  console.log(`Backend returned tooth ${tooth.tooth_number} with restorative:`, tooth.restorative);
                }
              });
              draft.tempToothData = newTempToothData;
            } else {
              console.warn('Backend did not return tooth_data in response, keeping local data');
              // Keep tempToothData as is (don't lose local changes)
              draft.chart.tooth_data = Object.values(draft.tempToothData);
            }
          }
        });
        
        set({
          isSaving: false,
          isDirty: false,
          lastSavedAt: new Date(),
          lastAutosaveAt: new Date(response.last_autosave_at),
          autosaveCount: response.autosave_count,
        });
        
        console.log('Chart saved successfully with restorative data:', response);
      } catch (error: any) {
        console.error('Save error:', error);
        set({
          error: error.response?.data?.error?.message || 'Failed to save chart',
          isSaving: false,
        });
        throw error;
      }
    },

    triggerAutosave: async () => {
      const { chart, isDirty, isSaving, tempToothData, inputMethod } = get();
      if (!chart || !isDirty || isSaving) return;
      
      set({ isSaving: true });
      
      try {
        const tooth_data = Object.values(tempToothData);
        
        console.log('Autosaving chart with tooth data:', tooth_data);
        
        // Log restorative data for debugging
        tooth_data.forEach(tooth => {
          if (tooth.restorative) {
            console.log(`Autosave - Tooth ${tooth.tooth_number} restorative:`, tooth.restorative);
          }
        });
        
        const response = await chartsApi.autosaveChart(chart.id, {
          tooth_data,
          clinical_notes: chart.clinical_notes || undefined,
          input_method: inputMethod || undefined,
          // AAP Classification
          aap_stage: chart.aap_stage || undefined,
          aap_grade: chart.aap_grade || undefined,
          aap_extent: chart.aap_extent || undefined,
          // Risk Factors
          risk_factors: chart.risk_factors || undefined,
          // Referral
          referral_recommended: chart.referral_recommended,
          referral_reason: chart.referral_reason || undefined,
        });
        
        console.log('Autosave API response:', response);
        console.log('Response tooth_data:', response.tooth_data);
        
        // IMPORTANT: Only sync if backend actually returns tooth_data
        set((draft) => {
          if (draft.chart) {
            // Sync AAP Classification from response
            if (response.aap_stage !== undefined) draft.chart.aap_stage = response.aap_stage;
            if (response.aap_grade !== undefined) draft.chart.aap_grade = response.aap_grade;
            if (response.aap_extent !== undefined) draft.chart.aap_extent = response.aap_extent;
            
            // Sync Risk Factors from response
            if (response.risk_factors !== undefined) draft.chart.risk_factors = response.risk_factors;
            
            // Sync Referral from response
            if (response.referral_recommended !== undefined) draft.chart.referral_recommended = response.referral_recommended;
            if (response.referral_reason !== undefined) draft.chart.referral_reason = response.referral_reason;
            
            // Check if response includes tooth_data
            if (response.tooth_data && Array.isArray(response.tooth_data)) {
              console.log('Syncing backend response back to tempToothData');
              
              // Update chart tooth_data
              draft.chart.tooth_data = response.tooth_data;
              
              // Rebuild tempToothData from backend response
              const newTempToothData: Record<number, ToothData> = {};
              response.tooth_data.forEach(tooth => {
                newTempToothData[tooth.tooth_number] = { ...tooth };
                
                // Log if restorative data is present
                if (tooth.restorative) {
                  console.log(`Backend returned tooth ${tooth.tooth_number} with restorative:`, tooth.restorative);
                }
              });
              draft.tempToothData = newTempToothData;
            } else {
              console.warn('Backend did not return tooth_data in response, keeping local data');
              // Keep tempToothData as is (don't lose local changes)
              draft.chart.tooth_data = Object.values(draft.tempToothData);
            }
          }
        });
        
        set({
          isSaving: false,
          isDirty: false,
          lastAutosaveAt: new Date(response.last_autosave_at),
          autosaveCount: response.autosave_count,
        });
        
        console.log('Autosave successful with restorative data');
      } catch (error) {
        set({ isSaving: false });
        console.error('Autosave failed:', error);
      }
    },

    finalizeChart: async (data: chartsApi.ChartFinalizeRequest) => {
      const { chart, isDirty, tempToothData } = get();
      if (!chart) throw new Error('No chart loaded');
      
      // Sync tempToothData to chart.tooth_data before finalizing
      set((draft) => {
        if (draft.chart) {
          draft.chart.tooth_data = Object.values(draft.tempToothData);
        }
      });
      
      // Save any pending changes first
      if (isDirty) {
        console.log('Saving pending changes before finalization...');
        await get().saveChart();
      }
      
      set({ isSaving: true, error: null });
      
      try {
        // Get tooth_data from tempToothData
        const tooth_data = Object.values(tempToothData);
        
        const finalizeData = {
          ...data,
          tooth_data,
          clinical_notes: chart.clinical_notes || undefined,
          aap_stage: data.aap_stage || chart.aap_stage || undefined,
          aap_grade: data.aap_grade || chart.aap_grade || undefined,
          aap_extent: data.aap_extent || chart.aap_extent || undefined,
        };
        
        const updatedChart = await chartsApi.finalizeChart(chart.id, finalizeData);
        
        // Update both chart and tempToothData with finalized data
        const newTempToothData: Record<number, ToothData> = {};
        updatedChart.tooth_data.forEach(tooth => {
          newTempToothData[tooth.tooth_number] = { ...tooth };
        });
        
        set({
          chart: updatedChart,
          tempToothData: newTempToothData,
          isSaving: false,
          isDirty: false,
          lastSavedAt: new Date(),
        });
        
        console.log('Chart finalized successfully:', updatedChart);
      } catch (error: any) {
        set({
          error: error.response?.data?.error?.message || 'Failed to finalize chart',
          isSaving: false,
        });
        throw error;
      }
    },

    acquireLock: async () => {
      const state = get();
      if (!state.chart) return false;
      
      try {
        await chartsApi.acquireChartLock(state.chart.id);
        return true;
      } catch (error) {
        return false;
      }
    },

    releaseLock: async () => {
      const state = get();
      if (!state.chart) return;
      
      try {
        await chartsApi.releaseChartLock(state.chart.id);
        set({ lockedBy: null });
      } catch (error) {
        console.error('Failed to release lock:', error);
      }
    },

    handleUserJoined: (user: { user_id: string; user_name: string }) => {
      set((state) => {
        if (!state.activeUsers.find((u) => u.user_id === user.user_id)) {
          state.activeUsers.push(user);
        }
      });
    },

    handleUserLeft: (userId: string) => {
      set((state) => {
        state.activeUsers = state.activeUsers.filter((u) => u.user_id !== userId);
      });
    },

    handleRemoteToothUpdate: (toothNumber: number, updates: Partial<ToothData>) => {
      set((state) => {
        if (!state.chart) return;
        
        // Update both chart and tempToothData
        const toothIndex = state.chart.tooth_data.findIndex(
          (t) => t.tooth_number === toothNumber
        );
        
        if (toothIndex >= 0) {
          Object.assign(state.chart.tooth_data[toothIndex], updates);
        }
        
        // Also update tempToothData
        if (state.tempToothData[toothNumber]) {
          Object.assign(state.tempToothData[toothNumber], updates);
        }
        // Note: Don't set isDirty for remote updates
      });
    },

    // Set chart data directly (useful for external/public views)
    setChart: (chartData: Chart) => {
      set((state) => {
        // Build tempToothData from chart's tooth_data
        const tempToothData: Record<number, ToothData> = {};
        if (chartData.tooth_data) {
          chartData.tooth_data.forEach(tooth => {
            tempToothData[tooth.tooth_number] = { ...tooth };
          });
        }
        
        state.chart = chartData;
        state.tempToothData = tempToothData;
        state.isDirty = false;
        state.lastSavedAt = new Date();
        
        console.log('Chart set with tempToothData:', tempToothData);
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);

// =============================================================================
// SELECTORS
// =============================================================================

export const useSelectedToothData = () => {
  return useChartStore((state) => {
    if (state.selectedTooth === null) return null;
    // Use tempToothData for selected tooth
    return state.tempToothData[state.selectedTooth] || null;
  });
};

export const useToothData = (toothNumber: number) => {
  return useChartStore((state) => {
    // Get data from tempToothData instead of chart
    return state.tempToothData[toothNumber] || null;
  });
};

// Add a new selector specifically for tooth status to ensure reactivity
export const useToothStatus = (toothNumber: number) => {
  return useChartStore((state) => {
    // Get status from tempToothData for better reactivity
    return state.tempToothData[toothNumber]?.status || 'present';
  });
};

export const useChartSummary = () => {
  return useChartStore((state) => {
    const toothData = Object.values(state.tempToothData);
    if (!toothData.length && !state.chart?.tooth_data.length) return null;

    // Use tempToothData if available, otherwise fall back to chart data
    const data = toothData.length > 0 ? toothData : (state.chart?.tooth_data || []);

    let teethPresent = 0;
    let teethMissing = 0;
    let teethImplants = 0;
    let sitesMeasured = 0;
    let sitesBleeding = 0;
    let totalDepth = 0;
    let maxDepth = 0;
    let pockets4mm = 0;
    let pockets5mm = 0;
    let pockets6mm = 0;
    let mobilityCount = 0;
    let furcationCount = 0;
    
    // NEW: Restorative counters
    let teethWithCrowns = 0;
    let teethWithFillings = 0;
    let teethWithCaries = 0;
    let teethNeedingTreatment = 0;
    let teethWithRootCanal = 0;

    for (const tooth of data) {
      if (tooth.status === 'missing') {
        teethMissing++;
        continue;
      }

      if (tooth.status === 'implant') {
        teethImplants++;
      }

      teethPresent++;

      // Periodontal stats
      if (tooth.mobility && tooth.mobility >= 1) mobilityCount++;
      if (tooth.furcation && tooth.furcation >= 1) furcationCount++;

      if (tooth.sites) {
        for (const site of tooth.sites) {
          if (site.pocket_depth !== null && site.pocket_depth !== undefined) {
            sitesMeasured++;
            totalDepth += site.pocket_depth;
            maxDepth = Math.max(maxDepth, site.pocket_depth);

            if (site.pocket_depth >= 4) pockets4mm++;
            if (site.pocket_depth >= 5) pockets5mm++;
            if (site.pocket_depth >= 6) pockets6mm++;
          }

          if (site.bleeding) sitesBleeding++;
        }
      }
      
      // NEW: Restorative stats
      if (tooth.restorative) {
        if (tooth.restorative.crown?.present) teethWithCrowns++;
        if ((tooth.restorative.fillings?.length || 0) > 0) teethWithFillings++;
        if (tooth.restorative.caries?.present) teethWithCaries++;
        if (tooth.restorative.treatment_needed?.procedure) teethNeedingTreatment++;
        if (tooth.restorative.root_canal?.treated) teethWithRootCanal++;
      }
    }

    const avgDepth = sitesMeasured > 0 ? totalDepth / sitesMeasured : 0;
    const bopPercentage = sitesMeasured > 0 ? (sitesBleeding / sitesMeasured) * 100 : 0;

    return {
      teeth_present: teethPresent,
      teeth_missing: teethMissing,
      teeth_implants: teethImplants,
      sites_measured: sitesMeasured,
      sites_bleeding: sitesBleeding,
      bop_percentage: Math.round(bopPercentage * 10) / 10,
      avg_pocket_depth: Math.round(avgDepth * 10) / 10,
      max_pocket_depth: maxDepth,
      pockets_4mm_plus: pockets4mm,
      pockets_5mm_plus: pockets5mm,
      pockets_6mm_plus: pockets6mm,
      mobility_grade_1_plus: mobilityCount,
      furcation_grade_1_plus: furcationCount,
      // NEW: Restorative summary
      teeth_with_crowns: teethWithCrowns,
      teeth_with_fillings: teethWithFillings,
      teeth_with_caries: teethWithCaries,
      teeth_needing_treatment: teethNeedingTreatment,
      teeth_with_root_canal: teethWithRootCanal,
    };
  });
};

export const useIsChartEditable = () => {
  return useChartStore(() => {
    // TEMPORARY: Always return true for testing
    return true;
    
    // Original logic (uncomment after fixing chart loading):
    // if (!state.chart) return false;
    // return ['draft', 'in_progress'].includes(state.chart.status);
  });
};
